/**
 * Shared constants used across the application
 */

// Mapping of attachment sides to their opposites
export const OPPOSITE_SIDES = {
  'left': 'right',
  'right': 'left',
  'front': 'back',
  'back': 'front'
};

// Wall thickness in meters — must match the value used in RoomBuilder.createWalls()
export const WALL_THICKNESS = 0.3;

// Number of panel attachment slots on a standard (non-L-shaped) module
export const STANDARD_MODULE_PANEL_SLOTS = 4;

// Components that live on an attachment point: the ghost tracks the surface under the
// cursor and hunts a component AP, and a lone one (?element=) shows its back until turned.
export const AP_MOUNTED_COMPONENT_TYPES = new Set([
  'panel', 'backrest', 'cushion', 'accessory-cushion', 'subcushion',
]);

// Upholstery on an AP: slides along the parent face, honours a `fixed` slot, and only
// obstructs or is obstructed by other upholstery (a cushion necessarily clips its backrest).
export const CUSHION_LIKE_COMPONENT_TYPES = new Set(['cushion', 'accessory-cushion']);

// A re-drag may move these to another free AP; a panel stays on its own, since it decides
// which sides of its module are panelled.
export const RESEATABLE_COMPONENT_TYPES = new Set([...CUSHION_LIKE_COMPONENT_TYPES, 'backrest']);

// Droppable straight onto the floor; everything else is cancelled when released un-snapped.
export function isFreePlaceable(componentType) {
  return componentType === 'module' || componentType === 'accessory';
}

// Stands on the floor in its own right, so it takes part in collision (blocks and is
// blocked) and turns about its own origin. An AP-mounted accessory rides its parent.
export function isFreeStanding(element) {
  const componentType = element?.furnitureData?.componentType;
  return componentType === 'module' ||
    (componentType === 'accessory' && !element.attachedTo);
}

// Grid groups that fold behind a disclosure chip: those marked `collapsed` in
// furniture.json's componentCategories, unless folding would leave the grid empty.
export function foldableGroups(sortedComponentTypes, componentCategories = {}) {
  const isCollapsed = (type) => componentCategories[type]?.collapsed === true;
  if (sortedComponentTypes.every(isCollapsed)) return new Set();
  return new Set(sortedComponentTypes.filter(isCollapsed));
}

// Wood finish for wood-surface items (oiko-wst / oiko-sta). The default is the value
// layoutCodec omits from saved layouts, so changing it re-reads every existing share link.
export const DEFAULT_WOOD_TYPE = 'dark';

// Fabric code new placements, thumbnails and order fallbacks use when nothing else is chosen.
export const DEFAULT_FABRIC_CODE = 'ET-14';

// environments.json key of the default studio — the only environment with a custom floor and room box.
export const WHITE_ROOM_ENVIRONMENT = 'white_room';

// Shown around a walled room where the sky is masked off; an environment's `backdropColor` overrides it.
export const DEFAULT_BACKDROP_COLOR = '#3a3a3a';

// Only "light" and "dark" are authored; anything else (stale state, a bad ?boardColor=)
// falls to the default rather than requesting a texture that doesn't exist.
export function normalizeWoodType(woodType) {
  return woodType === 'light' || woodType === 'dark' ? woodType : DEFAULT_WOOD_TYPE;
}

// Migrate legacy shadow-quality tiers (old PCF "soft"/"performance") onto the
// single VSM option. Single source of truth for the mapping.
export function migrateShadowQuality(quality) {
  return quality === 'soft' || quality === 'performance' ? 'vsm' : quality;
}

// The per-product API maps (`product-dimensions`, `product-ordering`, ...) are keyed by product id,
// but the diagon presets are one mirrored product, so the backend carries a single collection-level
// `diagon` row instead of a row per preset id. A diagon id with no row of its own resolves to that
// row rather than showing nothing; every other collection is looked up exactly.
export function lookupProductRow(map, id) {
  if (!map || !id) return null;
  return map[id] ?? (id.startsWith('diagon') ? map.diagon ?? null : null);
}

// Old preset ids kept reachable: links shared as `?preset=diagon` before it split into a mirrored
// pair land on the shop's default orientation. Renames only — an unknown id must stay unknown.
export const RENAMED_PRESETS = { diagon: 'diagon-l' };

// Visibility markers on catalog keys and ids (furniture.json collections and items,
// materials.json groups): `__` = dev-only, single `_` = disabled. The marker is stripped
// so it never leaks into a lookup key — collection ids join with layouts.json presets,
// the `<id>-collection` leg-heights key and saved state.
export function resolveCatalogKey(key, isDev) {
  const k = key || '';
  if (k.startsWith('__')) return { visible: !!isDev, key: k.slice(2), devOnly: true };
  if (k.startsWith('_')) return { visible: false, key: k.slice(1), devOnly: false };
  return { visible: true, key: k, devOnly: false };
}

// Baked AR ambient occlusion sits beside its model (scripts/bake-ao.mjs).
export const aoMapPath = (modelPath, pose = null) => modelPath.replace(/\.gltf$/, pose ? `.ao.${pose}.jpg` : '.ao.jpg');
